"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getReceivableReportContainer = void 0;
const common_1 = require("@finley-technologies/common");
const formatters_1 = require("./formatters");
const concentration_1 = require("./update/concentration");
const eligibility_1 = require("./update/eligibility");
const loanProductReport_1 = require("./update/loanProductReport");
const receivableReport_1 = require("./update/receivableReport");
const createEmptyReceivableReport = (borrowingBaseReportId) => ({
    assetValuation: 0,
    effectiveReceivableAdvanceRate: 0,
    effectiveReceivableValuationRate: 0,
    totalPerformingExposure: 0,
    weightedAverageReceivableAdvanceRate: 0,
    basicReport: {
        id: borrowingBaseReportId,
        borrowingBaseAmountAssets: 0,
        maxReceivableAdvanceAmount: 0,
        totalConcentrationKickout: 0,
        totalEligibilityKickout: 0,
        totalEligibleExposure: 0,
        totalReceivableExposure: 0,
    },
    assignedEligibleExposureSinceLastApprovedFundingRequest: 0,
});
const getReceivableReportContainer = ({ borrowingBaseReportId, eligibilityTestConfigs, concentrationLimitCalculationResults, multiLoanProductConcentrationLimits, timestamp, borrowingBaseReportForLatestApprovedFundingRequestMetadata, }) => {
    const receivableReport = createEmptyReceivableReport(borrowingBaseReportId);
    const loanProductMap = new Map();
    // Construct initial eligibility and concentration data structures
    const eligibilityTestResults = new Map(eligibilityTestConfigs.map((config) => [
        config.propertyKey,
        {
            eligibilityCriteriaKey: config.propertyKey,
            eligibilityReduction: 0,
        },
    ]));
    // Initializing calculation records here because these do not change during receivable processing
    // These are also used to generate the output for `getConcentrationLimitMultiLoanProductCalculations` and the id needs to be fixed here instead of potentially calling uuidv4() multiple times
    const concentrationLimitCalculations = (0, formatters_1.formatConcentration)({
        borrowingBaseReportId,
        concentrationLimitCalculationResults,
        timestamp,
    });
    // Setting up maps that will house required information for helping organize MLP concentration calculations
    // This will facilitate look up capabilities, particularly around what concentration limits are
    // multi-loan product and which loan product is associated with the limit
    const multiLoanProductConcentrationKickoutMap = new Map();
    for (const mlpConcentration of multiLoanProductConcentrationLimits) {
        const { loanProductIds, propertyKey } = mlpConcentration;
        multiLoanProductConcentrationKickoutMap.set(propertyKey, {
            loanProductIds: new Set(loanProductIds),
            kickoutsByLoanProductId: new Map(),
        });
    }
    return {
        addReceivable: (receivable) => {
            // Update receivable report (mutates existing report data)
            (0, receivableReport_1.updateReceivableReport)({
                receivableReport,
                receivable,
                borrowingBaseReportForLatestApprovedFundingRequestMetadata,
            });
            // Loan product borrowing base report update
            (0, loanProductReport_1.updateLoanProductMap)({ loanProductMap, receivable });
            // Eligibility update
            (0, eligibility_1.updateEligibilityTestResults)({ eligibilityTestResults, receivable });
            // Concentration update
            (0, concentration_1.updateConcentrationKickoutMap)({
                multiLoanProductConcentrationKickoutMap,
                receivable,
            });
        },
        getReceivableReport: () => receivableReport,
        getLoanProductReport: ({ loanProductId }) => {
            const loanProductData = loanProductMap.get(loanProductId);
            (0, common_1.finleyAssert)(loanProductData, `No receivable data found for loan product ID ${loanProductId}`);
            const receivableCount = loanProductData.externalReceivableIds.length;
            if (receivableCount === 0) {
                return null;
            }
            return loanProductData.loanProductReport;
        },
        getLoanProductExternalReceivableIds: ({ loanProductId }) => {
            const loanProductData = loanProductMap.get(loanProductId);
            (0, common_1.finleyAssert)(loanProductData, `No receivable data found for loan product ID ${loanProductId}`);
            return loanProductData.externalReceivableIds;
        },
        getEligibilityCriteriaCalculations: () => (0, formatters_1.formatEligibility)({
            borrowingBaseReportId,
            eligibilityTestResults,
            timestamp,
        }),
        getConcentrationLimitCalculations: () => concentrationLimitCalculations,
        getConcentrationLimitMultiLoanProductCalculations: () => (0, formatters_1.formatMultiLoanProductConcentration)({
            borrowingBaseReportId,
            concentrationLimitCalculations,
            multiLoanProductConcentrationKickoutMap,
            timestamp,
        }),
    };
};
exports.getReceivableReportContainer = getReceivableReportContainer;
//# sourceMappingURL=report.js.map